import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";
import { toast } from "sonner";

// Chave para armazenar páginas desbloqueadas na sessão
const getSessionKey = (page) => `ggee_page_unlocked_${page}`;

export default function PasswordProtection({ page, children, onLogout }) {
  const [showDialog, setShowDialog] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    checkPageAccess();
  }, [page]);

  const checkPageAccess = async () => {
    try {
      // Verificar se a página já foi desbloqueada nesta sessão
      const sessionKey = getSessionKey(page);
      const alreadyUnlocked = sessionStorage.getItem(sessionKey);
      
      if (alreadyUnlocked === 'true') {
        // Página já foi desbloqueada nesta sessão
        setAuthorized(true);
        setLoading(false);
        return;
      }

      // Buscar dados do perfil para ver se página tem senha
      const response = await api.get('/perfil/escola');
      const pagePasswords = response.data.page_passwords || {};
      
      // Se não tem senha configurada, libera acesso
      if (!pagePasswords[page]) {
        setAuthorized(true);
        setLoading(false);
        return;
      }

      // Tem senha e não foi desbloqueada ainda, mostrar dialog
      setShowDialog(true);
      setLoading(false);
    } catch (error) {
      console.error("Erro ao verificar acesso:", error);
      setLoading(false);
      // Se der erro de autenticação, o interceptor já cuida do redirecionamento
    }
  };

  const handleVerifyPassword = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error("Digite a senha");
      return;
    }

    setChecking(true);
    try {
      await api.post(`/page-password/verify?page=${page}&password=${password}`);

      // Senha correta, salvar na sessão e liberar acesso
      const sessionKey = getSessionKey(page);
      sessionStorage.setItem(sessionKey, 'true');
      
      setAuthorized(true);
      setShowDialog(false);
      toast.success("Acesso liberado!");
    } catch (error) {
      toast.error("Senha incorreta");
      setPassword("");
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verificando acesso...</p>
        </div>
      </div>
    );
  }

  if (!authorized) {
    return (
      <>
        <div className="flex items-center justify-center h-screen bg-gray-50">
          <div className="text-center p-8">
            <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Página Protegida</h2>
            <p className="text-gray-600">Aguardando autenticação...</p>
          </div>
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-blue-600" />
                Página Protegida por Senha
              </DialogTitle>
              <DialogDescription>
                Digite a senha para acessar a página de <span className="font-semibold capitalize">{page}</span>
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleVerifyPassword}>
              <div className="py-4">
                <Input
                  type="password"
                  placeholder="Digite a senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  disabled={checking}
                  className="text-lg py-6"
                />
              </div>

              <DialogFooter className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    window.history.back();
                  }}
                  disabled={checking}
                >
                  Voltar
                </Button>
                <Button
                  type="submit"
                  disabled={checking}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {checking ? "Verificando..." : "Desbloquear"}
                </Button>
              </DialogFooter>
            </form>

            <p className="text-xs text-gray-500 text-center mt-4">
              💡 A senha é configurada pelo administrador na página de Perfil
            </p>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return children;
}
